import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import api from "../services/api";
import { isSuperAdmin } from "../utils/role";

const Settings = () => {
  const [language, setLanguage] = useState("English");
  const [appearance, setAppearance] = useState("Light");
  const [twoFA, setTwoFA] = useState(true);
  const [mobilePush, setMobilePush] = useState(true);
  const [desktopNotif, setDesktopNotif] = useState(false);
  const [emailNotif, setEmailNotif] = useState(true);

  // Password update state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "New passwords do not match" });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: "error", text: "Password must be at least 6 characters long" });
      return;
    }

    setLoading(true);
    try {
      await api.put('/v1/admin/profile/password', {
        currentPassword,
        newPassword
      });

      setMessage({ type: "success", text: "Password updated successfully" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      // Optionally close the form on success
      // setShowPasswordForm(false);
    } catch (error) {
      console.error('Error updating password:', error.response?.status, error.message);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to update password"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>

          <div className="bg-white dark:bg-black rounded-xl shadow-sm">
            {/* Appearance */}
            <div className="flex items-center justify-between py-4 px-6">
              <div>
                <h3 className="font-medium text-gray-900">Appearance</h3>
                <p className="text-sm text-gray-500">
                  Customize how your theme looks on your device
                </p>
              </div>
              <select
                value={appearance}
                onChange={(e) => setAppearance(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option>Light</option>
                <option>Dark</option>
              </select>
            </div>

            {/* Language */}
            <div className="flex items-center justify-between py-4 px-6">
              <div>
                <h3 className="font-medium text-gray-900">Language</h3>
                <p className="text-sm text-gray-500">Select your language</p>
              </div>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option>English</option>
                <option>Swahili</option>
                <option>Yoruba</option>
              </select>
            </div>

            {/* Toggles */}
            {[
              ["Two-factor Authentication", "Keep your account secure by enabling 2FA via mail", twoFA, setTwoFA],
              ["Mobile Push Notifications", "Receive push notifications", mobilePush, setMobilePush],
              ["Desktop Notification", "Receive push notifications in desktop", desktopNotif, setDesktopNotif],
              ["Email Notifications", "Receive email notifications", emailNotif, setEmailNotif],
            ].map(([title, desc, value, setter], i) => (
              <div key={i} className="flex items-center justify-between py-4 px-6">
                <div>
                  <h3 className="font-medium text-gray-900">{title}</h3>
                  <p className="text-sm text-gray-500">{desc}</p>
                </div>
                <button
                  onClick={() => setter(!value)}
                  className={`w-12 h-6 flex items-center rounded-full p-1 transition ${value ? "bg-green-500" : "bg-gray-300"
                    }`}
                >
                  <span
                    className={`bg-white dark:bg-black w-4 h-4 rounded-full shadow-md transform transition ${value ? "translate-x-6" : ""
                      }`}
                  />
                </button>
              </div>
            ))}

            {/* Advanced settings */}
            <div className="py-4 px-6 border-t border-gray-100">
              <h3 className="font-medium text-gray-900 mb-4">Advanced Settings</h3>

              {/* Password Update Form */}
              <div className="mb-6 max-w-md">
                <button
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3 hover:text-gray-900 transition-colors focus:outline-none"
                >
                  Update Password
                  {showPasswordForm ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>

                {showPasswordForm && (
                  <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                    {message.text && (
                      <div className={`p-3 rounded-md mb-4 text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                        {message.text}
                      </div>
                    )}
                    <form onSubmit={handleUpdatePassword} className="space-y-4">
                      <div>
                        <input
                          type="password"
                          placeholder="Current Password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                          required
                        />
                      </div>
                      <div>
                        <input
                          type="password"
                          placeholder="New Password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                          required
                        />
                      </div>
                      <div>
                        <input
                          type="password"
                          placeholder="Confirm New Password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Updating...' : 'Update Password'}
                      </button>
                    </form>
                  </div>
                )}
              </div>

              {/* Only a super admin can create admins (the server enforces it too). */}
              {isSuperAdmin() && (
                <Link
                  to="/settings/add-admin"
                  className="text-gray-600 text-sm font-medium underline hover:text-gray-900"
                >
                  Add new admin
                </Link>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Settings;
